import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import {
  usePreviewTracks,
  useMediaDevices,
} from '@livekit/components-react'
import { Track, LocalVideoTrack, LocalAudioTrack } from 'livekit-client'

type DevicePreviewProps = {
  className?: string
}

/**
 * DevicePreview component using LiveKit's official preview hooks.
 * This ensures the same video/audio pipeline as the actual simulation.
 */
export const DevicePreview: React.FC<DevicePreviewProps> = ({ className = '' }) => {
  // Track enabled state
  const [cameraEnabled, setCameraEnabled] = useState(true)
  const [micEnabled, setMicEnabled] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [permissionDenied, setPermissionDenied] = useState(false)

  // Selected devices
  const [selectedCamera, setSelectedCamera] = useState<string>('')
  const [selectedMicrophone, setSelectedMicrophone] = useState<string>('')
  const [selectedSpeaker, setSelectedSpeaker] = useState<string>('')

  // Video container ref for attaching LiveKit video track
  const videoContainerRef = useRef<HTMLDivElement>(null)

  // Audio level monitoring
  const [audioLevel, setAudioLevel] = useState(0)
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const animationFrameRef = useRef<number | null>(null)

  // Get available devices using LiveKit's hook
  const cameras = useMediaDevices({ kind: 'videoinput' })
  const microphones = useMediaDevices({ kind: 'audioinput' })
  const speakers = useMediaDevices({ kind: 'audiooutput' })

  // Create preview track options - memoized to prevent unnecessary re-renders
  const trackOptions = useMemo(() => ({
    audio: micEnabled ? {
      deviceId: selectedMicrophone || undefined,
      autoGainControl: true,
      echoCancellation: true,
      noiseSuppression: true,
    } : false,
    video: cameraEnabled ? {
      deviceId: selectedCamera || undefined,
      facingMode: (selectedCamera ? undefined : 'user') as 'user' | 'environment' | undefined,
      resolution: {
        width: 640,
        height: 480,
        frameRate: 24,
      },
    } : false,
  }), [cameraEnabled, micEnabled, selectedCamera, selectedMicrophone])

  // Handle preview track errors
  const handlePreviewError = useCallback((err: Error) => {
    console.error('DevicePreview: Track error:', err)
    if (err.name === 'NotAllowedError' || err.message.includes('Permission')) {
      setPermissionDenied(true)
      setError('Camera/microphone permission denied. Please allow access in your browser settings.')
    } else {
      setError(err.message || 'Failed to access camera/microphone')
    }
  }, [])

  // Use LiveKit's usePreviewTracks hook - this is the same mechanism used in the actual simulation
  const tracks = usePreviewTracks(trackOptions, handlePreviewError)

  // Extract video and audio tracks
  const videoTrack = useMemo(() => {
    return tracks?.find((t): t is LocalVideoTrack => t.kind === Track.Kind.Video) || null
  }, [tracks])

  const audioTrack = useMemo(() => {
    return tracks?.find((t): t is LocalAudioTrack => t.kind === Track.Kind.Audio) || null
  }, [tracks])

  // Clear error when tracks are successfully created
  useEffect(() => {
    if (tracks && tracks.length > 0) {
      setError(null)
      setPermissionDenied(false)
    }
  }, [tracks])

  // Attach video track to container - same approach as the actual simulation
  useEffect(() => {
    if (videoContainerRef.current && videoTrack) {
      // Clear any existing video elements
      videoContainerRef.current.innerHTML = ''

      // Use LiveKit's attach() method - this is the same rendering pipeline as the simulation
      const videoElement = videoTrack.attach()
      videoElement.className = 'w-full h-full object-cover'
      videoElement.style.transform = 'scaleX(-1)' // Mirror for self-view
      videoContainerRef.current.appendChild(videoElement)

      return () => {
        // Detach when track changes or component unmounts
        videoTrack.detach()
      }
    }
  }, [videoTrack])

  // Set default selected devices when device lists become available
  useEffect(() => {
    if (cameras.length > 0 && !selectedCamera) {
      setSelectedCamera(cameras[0].deviceId)
    }
  }, [cameras, selectedCamera])

  useEffect(() => {
    if (microphones.length > 0 && !selectedMicrophone) {
      setSelectedMicrophone(microphones[0].deviceId)
    }
  }, [microphones, selectedMicrophone])

  useEffect(() => {
    if (speakers.length > 0 && !selectedSpeaker) {
      setSelectedSpeaker(speakers[0].deviceId)
    }
  }, [speakers, selectedSpeaker])

  // Audio level monitoring
  useEffect(() => {
    if (audioTrack && micEnabled) {
      startAudioMonitoring(audioTrack)
    } else {
      stopAudioMonitoring()
    }

    return () => {
      stopAudioMonitoring()
    }
  }, [audioTrack, micEnabled])

  const startAudioMonitoring = useCallback((track: LocalAudioTrack) => {
    try {
      const mediaStreamTrack = track.mediaStreamTrack
      if (!mediaStreamTrack) {
        console.warn('No media stream track available for audio monitoring')
        return
      }

      const stream = new MediaStream([mediaStreamTrack])

      if (!audioContextRef.current) {
        const AudioContextClass =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
        audioContextRef.current = new AudioContextClass()
      }

      const audioContext = audioContextRef.current
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 256
      analyserRef.current = analyser

      const source = audioContext.createMediaStreamSource(stream)
      source.connect(analyser)

      const dataArray = new Uint8Array(analyser.frequencyBinCount)

      const updateLevel = () => {
        analyser.getByteFrequencyData(dataArray)
        const average = dataArray.reduce((sum, val) => sum + val, 0) / dataArray.length
        const normalizedLevel = Math.min(100, Math.round((average / 128) * 100))
        setAudioLevel(normalizedLevel)
        animationFrameRef.current = requestAnimationFrame(updateLevel)
      }

      updateLevel()
    } catch (err) {
      console.error('Failed to start audio monitoring:', err)
    }
  }, [])

  const stopAudioMonitoring = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    setAudioLevel(0)
  }, [])

  // Play test sound for speaker testing
  const playTestSound = useCallback(async () => {
    try {
      const AudioContextClass =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      const audioContext = new AudioContextClass()
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)

      oscillator.type = 'sine'
      oscillator.frequency.value = 440 // A4 note
      gainNode.gain.value = 0.3

      // Set speaker if available
      if (selectedSpeaker && 'setSinkId' in audioContext.destination) {
        try {
          await (
            audioContext.destination as unknown as { setSinkId: (id: string) => Promise<void> }
          ).setSinkId(selectedSpeaker)
        } catch (err) {
          console.warn('Could not set audio output device:', err)
        }
      }

      oscillator.start()

      // Play for 500ms then fade out
      setTimeout(() => {
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1)
        setTimeout(() => {
          oscillator.stop()
          audioContext.close()
        }, 100)
      }, 400)
    } catch (err) {
      console.error('Failed to play test sound:', err)
      setError('Failed to play test sound')
    }
  }, [selectedSpeaker])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAudioMonitoring()
      if (audioContextRef.current) {
        audioContextRef.current.close()
        audioContextRef.current = null
      }
    }
  }, [stopAudioMonitoring])

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-5 ${className}`}>
      <h3 className="text-base font-semibold text-gray-900 mb-4">Test Your Devices</h3>

      {/* Permission denied warning */}
      {permissionDenied && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start gap-2">
            <svg
              className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div className="text-sm text-yellow-800">
              <p className="font-medium">Permission required</p>
              <p className="mt-1">
                Please allow camera and microphone access in your browser to test your devices.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && !permissionDenied && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Camera Preview */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Camera</span>
            <button
              onClick={() => setCameraEnabled(!cameraEnabled)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                cameraEnabled
                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {cameraEnabled ? 'On' : 'Off'}
            </button>
          </div>

          <div className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden">
            {cameraEnabled && videoTrack ? (
              <div ref={videoContainerRef} className="w-full h-full" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <svg
                    className="w-10 h-10 text-gray-600 mx-auto mb-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="text-xs text-gray-500">Camera off</p>
                </div>
              </div>
            )}
          </div>

          {/* Camera selector */}
          {cameras.length > 1 && (
            <select
              value={selectedCamera}
              onChange={(e) => setSelectedCamera(e.target.value)}
              className="mt-2 w-full text-xs border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {cameras.map((camera) => (
                <option key={camera.deviceId} value={camera.deviceId}>
                  {camera.label}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Microphone & Speaker */}
        <div className="space-y-4">
          {/* Microphone */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Microphone</span>
              <button
                onClick={() => setMicEnabled(!micEnabled)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  micEnabled
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {micEnabled ? 'On' : 'Off'}
              </button>
            </div>

            {/* Audio level meter */}
            <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-green-400 to-green-600 transition-all duration-75"
                style={{ width: `${audioLevel}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {micEnabled ? 'Speak to test your microphone' : 'Click "On" to test'}
            </p>

            {/* Microphone selector */}
            {microphones.length > 1 && (
              <select
                value={selectedMicrophone}
                onChange={(e) => setSelectedMicrophone(e.target.value)}
                className="mt-2 w-full text-xs border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {microphones.map((mic) => (
                  <option key={mic.deviceId} value={mic.deviceId}>
                    {mic.label}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Speaker */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Speaker</span>
              <button
                onClick={playTestSound}
                className="px-3 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors flex items-center gap-1"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                  />
                </svg>
                Test Sound
              </button>
            </div>

            {/* Speaker selector */}
            {speakers.length > 0 && (
              <select
                value={selectedSpeaker}
                onChange={(e) => setSelectedSpeaker(e.target.value)}
                className="w-full text-xs border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {speakers.map((speaker) => (
                  <option key={speaker.deviceId} value={speaker.deviceId}>
                    {speaker.label}
                  </option>
                ))}
              </select>
            )}
            {speakers.length === 0 && (
              <p className="text-xs text-gray-500">
                Speaker selection not available in this browser
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Status indicator */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <div
              className={`w-2 h-2 rounded-full ${cameraEnabled && videoTrack ? 'bg-green-500' : 'bg-gray-300'}`}
            />
            <span className="text-gray-600">Camera</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div
              className={`w-2 h-2 rounded-full ${micEnabled && audioTrack ? 'bg-green-500' : 'bg-gray-300'}`}
            />
            <span className="text-gray-600">Microphone</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DevicePreview
